import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Deal } from "../../lib/types";
import DealLink from "../DealLink";
import DealAgeChips from "../shared/DealAgeChips";
import DealCallText from "../DealCallText";
import { formatPhone } from "../../lib/phoneFormat";
import { approveDeal, rejectDeal } from "../../lib/asmActions";
import { useAuth } from "../../context/AuthContext";
import ManualEntryModal, { type RosterMember } from "../deal/ManualEntryModal";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

/**
 * Alex's ASM View — Dual Gate system.
 *
 * Gate 1: pending_asm_review — new SalesRabbit appointments needing ASM approval.
 *   Approve → appointment_received (fires Text 1, generates portal URL)
 *   Reject → logs rejection reason, notifies RM
 *
 * Gate 2: report_approved — drone reports that passed QC, ready for lead conversion.
 *   Convert to Lead → initial_contact (leads phase)
 */

type ASMRow = Pick<
  Deal,
  | "id"
  | "homeowner_name"
  | "homeowner_phone"
  | "property_street"
  | "property_city"
  | "property_state"
  | "stage"
  | "days_in_stage"
  | "stage_entered_at"
  | "phase_entered_at"
  | "owner_id"
  | "split_rm_id"
  | "appointment_date"
  | "drone_completed_date"
  | "imging_report_link"
  | "asm_review_status"
  | "created_at"
  | "canvasser_name"
  | "claim_number"
>;

const COLS =
  "id,homeowner_name,homeowner_phone,property_street,property_city,property_state,stage,days_in_stage,stage_entered_at,phase_entered_at,owner_id,split_rm_id,appointment_date,drone_completed_date,imging_report_link,asm_review_status,created_at,canvasser_name,claim_number";

// ── Main View ───────────────────────────────────────────────────────────────

export default function AlexASMView() {
  const { member } = useAuth();
  const [deals, setDeals] = useState<ASMRow[] | null>(null);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [rosterMembers, setRosterMembers] = useState<RosterMember[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [{ data, error }, { data: mems }] = await Promise.all([
        supabase
          .from("deals")
          .select(COLS)
          .or("stage.eq.pending_asm_review,stage.eq.report_approved")
          .order("created_at", { ascending: true }),
        supabase
          .from("team_roster")
          .select("id,full_name,role")
          .eq("is_active", true)
          .order("full_name"),
      ]);
      if (error) {
        if (!cancelled) setErr(error.message);
        return;
      }
      if (!cancelled) {
        setDeals((data ?? []) as unknown as ASMRow[]);
        const map: Record<string, string> = {};
        const roster: RosterMember[] = [];
        (mems ?? []).forEach((m) => {
          map[m.id] = m.full_name;
          roster.push({ id: m.id, full_name: m.full_name, role: m.role ?? "" });
        });
        setMembers(map);
        setRosterMembers(roster);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Gate 1 — pending ASM review
  const gate1 = useMemo(
    () => (deals ?? []).filter((d) => d.stage === "pending_asm_review"),
    [deals],
  );

  // Gate 2 — report approved, ready for lead conversion
  const gate2 = useMemo(
    () => (deals ?? []).filter((d) => d.stage === "report_approved"),
    [deals],
  );

  // Approve / Reject route through lib/asmActions, which handles auth header,
  // friendly error messaging (including HTML proxy-intercept detection), and
  // captures the actor for the audit trail.
  async function handleApprove(dealId: string) {
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      await approveDeal(dealId, member?.id ?? null);
      reload();
    } catch (e: any) {
      alert(`Approve failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  async function handleReject(dealId: string) {
    const reason = rejectReason.trim();
    if (!reason) {
      alert("Please enter a rejection reason.");
      return;
    }
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      await rejectDeal(dealId, reason, member?.id ?? null);
      setRejectingId(null);
      setRejectReason("");
      reload();
    } catch (e: any) {
      alert(`Reject failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  async function handleConvertToLead(dealId: string) {
    if (!window.confirm("Convert this deal to a lead? This moves it to the Leads phase.")) return;
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      const { error } = await supabase
        .from("deals")
        .update({ stage: "initial_contact", phase: "leads", days_in_stage: 0 })
        .eq("id", dealId);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "Converted to Lead",
        description: "ASM Gate 2: Report approved → Lead conversion",
        metadata: { from: "report_approved", to: "initial_contact", gate: "asm_gate_2" },
      });
      reload();
    } catch (e: any) {
      alert(`Conversion failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  if (err) return <div className="bg-red-50 text-red-700 p-4 rounded">{err}</div>;
  if (!deals)
    return <div className="text-slate-500 py-8 text-center text-sm">Loading ASM review…</div>;

  return (
    <div className="space-y-6">
      {/* Manual entry modal */}
      {showManualEntry && (
        <ManualEntryModal
          onClose={() => setShowManualEntry(false)}
          onCreated={() => { setShowManualEntry(false); reload(); }}
          rosterMembers={rosterMembers}
          actorId={member?.id ?? null}
        />
      )}

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2">
        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-200 text-slate-700">
          Gate 1 — Intake: {gate1.length}
        </span>
        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
          Gate 2 — Convert: {gate2.length}
        </span>
      </div>

      {/* ── GATE 1: Pending ASM Review ── */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-bardin-ink">Gate 1 — Appointment Intake Review</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
            Verify territory, legitimacy, correct people
          </span>
          <button
            onClick={() => setShowManualEntry(true)}
            className="ml-auto flex items-center gap-1 px-3 py-1 rounded-lg bg-bardin-orange text-white text-xs font-semibold hover:opacity-90 transition"
          >
            <span className="text-base leading-none">+</span> Manual Entry
          </button>
        </div>

        {gate1.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-4 text-sm text-slate-500 text-center">
            No appointments pending review.
          </div>
        ) : (
          <div className="space-y-2">
            {gate1.map((d) => {
              const ownerName = d.owner_id ? members[d.owner_id] : null;
              const isRejecting = rejectingId === d.id;
              const isBusy = !!busy[d.id];

              return (
                <div key={d.id} className="bg-white rounded-xl shadow px-4 py-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-bardin-ink">
                          <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                          Pending Review
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {isAddressLinkable(d) ? (
                          <a
                            href={mapsHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-700"
                          >
                            {[d.property_street, d.property_city, d.property_state].filter(Boolean).join(", ")}
                          </a>
                        ) : (
                          [d.property_street, d.property_city, d.property_state].filter(Boolean).join(", ") || "—"
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500 mt-1">
                        {ownerName && <span>RM: {ownerName}</span>}
                        {d.canvasser_name && <span>Canvasser: {d.canvasser_name}</span>}
                        {d.appointment_date && (
                          <span>
                            Appt: {new Date(d.appointment_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </span>
                        )}
                        {d.homeowner_phone && <span>{formatPhone(d.homeowner_phone)}</span>}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <DealAgeChips deal={d} align="right" />
                    </div>
                  </div>

                  {/* Action buttons */}
                  {!isRejecting ? (
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={() => handleApprove(d.id)}
                        disabled={isBusy}
                        className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
                      >
                        {isBusy ? "Approving…" : "Approve"}
                      </button>
                      <button
                        onClick={() => { setRejectingId(d.id); setRejectReason(""); }}
                        disabled={isBusy}
                        className="px-4 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-medium border border-red-200 hover:bg-red-100 transition disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <div className="ml-auto">
                        <DealCallText dealId={d.id} homeownerName={d.homeowner_name} homeownerPhone={d.homeowner_phone} />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="text"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Rejection reason…"
                        className="flex-1 border border-slate-300 rounded px-3 py-1.5 text-xs"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") handleReject(d.id); }}
                      />
                      <button
                        onClick={() => handleReject(d.id)}
                        disabled={isBusy}
                        className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition disabled:opacity-50"
                      >
                        {isBusy ? "…" : "Confirm Reject"}
                      </button>
                      <button
                        onClick={() => setRejectingId(null)}
                        className="text-xs text-slate-400 hover:text-slate-600"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── GATE 2: Report Approved → Convert to Lead ── */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-bardin-ink">Gate 2 — Report Approved → Lead Conversion</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
            Confirm RM/TL ownership, convert to lead
          </span>
        </div>

        {gate2.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-4 text-sm text-slate-500 text-center">
            No reports ready for lead conversion.
          </div>
        ) : (
          <div className="space-y-2">
            {gate2.map((d) => {
              const ownerName = d.owner_id ? members[d.owner_id] : null;
              const splitName = d.split_rm_id ? members[d.split_rm_id] : null;
              const isBusy = !!busy[d.id];

              return (
                <div key={d.id} className="bg-white rounded-xl shadow px-4 py-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-bardin-ink">
                          <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-800">
                          Report Approved
                        </span>
                        {d.claim_number && (
                          <span className="text-[10px] text-slate-400">#{d.claim_number}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {isAddressLinkable(d) ? (
                          <a
                            href={mapsHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-700"
                          >
                            {[d.property_street, d.property_city].filter(Boolean).join(", ")}
                          </a>
                        ) : (
                          [d.property_street, d.property_city].filter(Boolean).join(", ") || "—"
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500 mt-1">
                        {ownerName && <span>RM: {ownerName}</span>}
                        {splitName && <span>Split: {splitName}</span>}
                        {d.imging_report_link && <span className="text-emerald-600">Report linked</span>}
                        {d.drone_completed_date && (
                          <span>
                            Flown: {new Date(d.drone_completed_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => handleConvertToLead(d.id)}
                      disabled={isBusy}
                      className="px-4 py-1.5 rounded-lg bg-bardin-teal text-white text-xs font-semibold hover:opacity-90 transition disabled:opacity-50"
                    >
                      {isBusy ? "Converting…" : "Convert to Lead"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
